import { MessageType, PartialMessage } from '@protobuf-ts/runtime'
import { Filter } from 'components/core/search-filter/filter'
import { useLocalStorageContext } from 'hooks/local-storage-context'
import { emptyCancel } from 'lib/cancel'
import { MessageTypeType, MetaFields } from 'lib/search'
import { SlidersHorizontal } from 'lucide-react'
import { useRouter } from 'next/router'
import React, { useCallback, useEffect, useMemo, useState } from 'react'

export const FILTER_PICKER_STORAGE_KEY = 'filter-picker-storage-key'

export type FilterPickerProps<T extends MessageType<object>> = {
    filterOptions: string[]
    filterKeyToLabel: Record<string, string>
    onSelectedFiltersChange: (selectedFilters: string[]) => void
    onFilterChange: (filter: PartialMessage<T>) => void
    request: T
    fieldToFilterMap: Partial<Record<keyof MessageTypeType<T>, string>>
    urlDecode?: (k: string, v: unknown) => unknown,
    id: string // uniquely identified the filter picker/table
}
export const FilterPicker = <T extends MessageType<object>>({
    filterKeyToLabel, // maps filter id to display label
    filterOptions,
    onSelectedFiltersChange: onChange,
    onFilterChange,
    request,
    fieldToFilterMap, // maps field name in the request proto to its filter id
    urlDecode,
    id,
}: FilterPickerProps<T>): React.ReactElement => {
    const [options, setOptions] = useState<string[]>(filterOptions)
    const [selectedFiltersMap, setSelectedFiltersMap] = useLocalStorageContext(FILTER_PICKER_STORAGE_KEY, {})
    const selectedFilters = useMemo(() => selectedFiltersMap?.[id] || [], [selectedFiltersMap, id])
    const filterToFieldMap = reverseMap(fieldToFilterMap as Record<string, string>)

    const initialDisplayFilters = useGetInitialDisplayFilters(
        request,
        urlDecode,
        fieldToFilterMap,
    )

    // this loads filters from query params and local storage on load
    useEffect(() => {
        const currentFilters = selectedFiltersMap?.[id] || []
        const combinedFilters = Array.from(new Set([...currentFilters, ...initialDisplayFilters]))
        setSelectedFiltersMap({ ...selectedFiltersMap, [id]: combinedFilters })
        onChange(combinedFilters)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id, initialDisplayFilters, onChange, setSelectedFiltersMap])

    const onChangeHandler = useCallback(
        (_selectedFilters: string[]): void => {
            const previousFilters = selectedFiltersMap?.[id] || []
            const removedFilters = previousFilters.filter((f) => !_selectedFilters.includes(f))

            // If filters were removed, reset them to undefined
            if (removedFilters.length > 0) {
                const fieldsToClear: Record<string, unknown> = {}

                removedFilters.forEach((filterId) => {
                    const protoFields = filterToFieldMap[filterId]
                    if (protoFields) {
                        protoFields.forEach((fieldName) => {
                            fieldsToClear[fieldName] = undefined
                        })
                    }
                })

                // Call onFilterChange with the fields to clear
                onFilterChange(fieldsToClear as PartialMessage<T>)
            }

            setSelectedFiltersMap({ ...selectedFiltersMap, [id]: _selectedFilters })
            onChange(_selectedFilters)
        },
        [onChange, onFilterChange, id, selectedFiltersMap, setSelectedFiltersMap, filterToFieldMap],
    )

    const search = useCallback(
        (searchQuery: string, _exclude: string[]): (() => void) => {
            if (!searchQuery.trim()) {
                setOptions(filterOptions)
                return emptyCancel
            }
            setOptions(filterOptions.filter((label) => label.toLowerCase().includes(searchQuery.toLowerCase())))
            return emptyCancel
        },
        [filterOptions],
    )

    return <Filter<string>
        label="Filters"
        options={options}
        selected={selectedFilters}
        onChange={onChangeHandler}
        search={search}
        renderLabel={(label: string): string => filterKeyToLabel[label] || label}
        LeftIcon={SlidersHorizontal}
        showClearButton
        showSelectedInButton={false}
    />
}

export const useGetInitialDisplayFilters = <T extends MessageType<object>>(
    i: T,
    urlDecode?: (k: string, v: unknown) => unknown,
    fieldToFilterMap?: Partial<Record<keyof MessageTypeType<T>, string>>,
): string[] => {
    const router = useRouter()
    const query = router.query
    const filtersToDisplay: string[] = []

    for (const f of i.fields || []) {
        if (MetaFields.includes(f.localName as MetaFields)) {
            continue
        }

        let value = query[f.localName]
        if (!value) {
            continue
        }

        if (f.repeat && !Array.isArray(value)) {
            value = [value]
        }
        if (urlDecode) {
            value = urlDecode(f.localName, value) as string
        }
        const filterName = fieldToFilterMap?.[f.localName as keyof MessageTypeType<T>]
        if (filterName) {
            filtersToDisplay.push(filterName)
        }
    }

    // componentDidMount equivalent
    return useState(() => filtersToDisplay)[0]
}

const reverseMap = (originalMap: Record<string, string>): Record<string, string[]> => {
    const reversedMap: Record<string, string[]> = {}
    Object.entries(originalMap as Record<string, string>).forEach(([fieldName, filterId]) => {
        if (filterId) {
            if (!reversedMap[filterId]) {
                reversedMap[filterId] = []
            }
            reversedMap[filterId].push(fieldName)
        }
    })
    return reversedMap
}
